// MaturityLadder — 5-segment horizontal bar showing a tenant's current
// maturity level vs. the target this framework commits them to.
//
// Spec: specs/consumer-frameworks-strings.md § Maturity ladder.
// Vocabulary: initial · developing · defined · managed · optimizing
// (reuses shared/MaturityLevel from personae-api).
//
// Rendering rules:
//   - fill `--accent` up to `current` (inclusive)
//   - fill `--brand` at 42% opacity for the `target` segment
//   - leave `--surface2` beyond
//   - always paired with a text label (never colour alone)
//
// `current` may be null when the tenant has no recorded level in this
// domain yet (see consumer-mvp-plan.md § open items on tenant.current_level).
// The ladder then renders all segments as unfilled, with the target
// highlighted — the UI still communicates "this framework targets {level}"
// without lying about where the tenant currently stands.

import { ALL_MATURITY_LEVELS, type MaturityLevel } from '@gc-coach/shared';
import styles from './MaturityLadder.module.css';

interface Props {
  current: MaturityLevel | null;
  target: MaturityLevel;
  compact?: boolean;
}

export function MaturityLadder({ current, target, compact = false }: Props) {
  const currentIdx = current ? ALL_MATURITY_LEVELS.indexOf(current) : -1;
  const targetIdx = ALL_MATURITY_LEVELS.indexOf(target);

  return (
    <div className={compact ? styles.wrapCompact : styles.wrap} aria-label={`Maturity: current ${current ?? 'unknown'}, target ${target}`}>
      {ALL_MATURITY_LEVELS.map((level, i) => {
        const filled = currentIdx >= 0 && i <= currentIdx;
        const isTarget = i === targetIdx;
        return (
          <span
            key={level}
            className={[
              styles.seg,
              filled ? styles.filled : '',
              isTarget ? styles.target : '',
            ]
              .filter(Boolean)
              .join(' ')}
          />
        );
      })}
    </div>
  );
}
